import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, Trash2, Lock, Loader2, Wifi } from 'lucide-react';
import { HostSettingsCard } from '../../components/HostSettingsCard';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  openCheckout,
  onCheckoutClosed,
  closeCheckout,
  isNativeCheckoutSupported,
} from '../../utils/openCheckout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const cardBrandLabel = (brand) => {
  if (!brand) return 'Card';
  const text = String(brand).toLowerCase();
  if (text.includes('visa')) return 'Visa';
  if (text.includes('master')) return 'Mastercard';
  if (text.includes('amex') || text.includes('american')) return 'Amex';
  if (text.includes('maestro')) return 'Maestro';
  return brand;
};

const formatExpiry = (month, year) => {
  if (!month || !year) return '';
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
};

export default function GuestPaymentMethodPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [isDemoEnv, setIsDemoEnv] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    // Fire-and-forget: detect whether Viva is in demo mode so we can show the
    // Closed Beta test-card banner. Auto-hides once we flip to LIVE.
    axios.get(`${API}/config/runtime`)
      .then((res) => setIsDemoEnv(res.data?.payment_environment === 'demo'))
      .catch(() => { /* banner just stays hidden if config fails */ });
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/guest/payment-method`, { headers: authHeaders() });
      setCard(res.data && res.data.connected ? res.data : null);
    } catch (err) {
      toast.error('Failed to load payment method');
    } finally {
      setLoading(false);
    }
  }, []);

  const reconcile = useCallback(async ({ silent = false } = {}) => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/guest/payment-method/reconcile`, {}, { headers: authHeaders() });
      if (res.data?.reconciled) {
        if (!silent) toast.success('Card recovered from your last payment');
        setCard(res.data.connected ? res.data : null);
        await refresh();
        return true;
      }
      if (!silent) {
        toast.info('No pending card-save attempts to recover');
      }
      return false;
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || 'Could not recover card');
      return false;
    } finally {
      setReconciling(false);
    }
  }, [refresh]);

  useEffect(() => {
    (async () => {
      await refresh();
    })();
  }, [refresh]);

  // Auto-reconcile once on mount when no card is connected — catches the
  // case where the Viva redirect dropped the `t` param (e.g. dashboard
  // Source URL not yet pointed at /payment-complete). Runs silently so we
  // never bother the user with toasts on first load.
  const autoReconciledRef = useRef(false);
  useEffect(() => {
    if (loading || card || autoReconciledRef.current) return;
    autoReconciledRef.current = true;
    reconcile({ silent: true });
  }, [loading, card, reconcile]);

  const handleAddCard = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/guest/payment-method/init`, {}, { headers: authHeaders() });
      if (res.data?.checkout_url) {
        // Native app: pop the Viva Smart Checkout in Chrome Custom Tabs /
        // SFSafariViewController so the guest never leaves Allure. When
        // they dismiss the browser we silently pull the freshest card
        // state and, if Viva already saved the card, the empty-state UI
        // flips to "connected" without another tap.
        if (isNativeCheckoutSupported()) {
          // Native flow: we open the Viva Smart Checkout in a Chrome
          // Custom Tab (Android) / SFSafariViewController (iOS). Two
          // parallel signals drive the "browser is done" logic:
          //
          //   (a) Server-side reconcile poll — every 2.5s while the
          //       tab is open, the main WebView asks the backend
          //       whether Viva has finalized the txn for this user.
          //       As soon as it has, we programmatically call
          //       `Browser.close()` so the user NEVER sees the Allure
          //       site load inside Chrome (which would otherwise pop
          //       Chrome's "Use saved passkey?" sheet for whichever
          //       account the browser was last logged into, and
          //       leave the user stuck tapping ✕ manually).
          //
          //   (b) `browserFinished` listener — a safety net for the
          //       cancel path (user closes the tab manually before
          //       submitting the card). Also re-runs a silent
          //       reconcile in case (a) missed the race.
          let closingSelf = false;
          let pollTimer = null;
          let pollStopped = false;

          const stopPolling = () => {
            pollStopped = true;
            if (pollTimer) {
              clearTimeout(pollTimer);
              pollTimer = null;
            }
          };

          const pollForConnection = async () => {
            if (pollStopped) return;
            try {
              const recon = await axios.post(
                `${API}/guest/payment-method/reconcile`,
                {},
                { headers: authHeaders() },
              );
              if (recon.data?.reconciled) {
                stopPolling();
                closingSelf = true;
                // Close the Chrome Custom Tab from the native side.
                // Fires the `browserFinished` event, which our
                // onCheckoutClosed handler below then picks up to
                // refresh the card UI.
                try { await closeCheckout(); } catch (_e) { /* noop */ }
                return;
              }
            } catch (_err) {
              // Ignore — reconcile is best-effort while polling.
            }
            if (!pollStopped) {
              pollTimer = setTimeout(pollForConnection, 2500);
            }
          };

          const unsubscribe = onCheckoutClosed(async () => {
            unsubscribe();
            stopPolling();
            try { localStorage.removeItem('allure_native_checkout_pending'); } catch (_e1) { /* noop */ }
            setBusy(false);
            const recovered = await reconcile({ silent: true });
            if (recovered) {
              toast.success('Card saved \u2014 you\u2019re ready to pay in-app');
            } else if (!closingSelf) {
              // Fetch the freshest read regardless (Viva sometimes finalizes
              // via /payment-complete asynchronously; the card may have landed
              // in Mongo already).
              await refresh();
            } else {
              // We auto-closed after a successful reconcile — just refresh
              // to render the connected card.
              await refresh();
            }
          });
          try {
            // Flag for PaymentCompletePage: when Viva redirects back inside
            // the Custom Tab we must NOT navigate (which would render the
            // full app inside Chrome). The flag is cleared there immediately.
            try { localStorage.setItem('allure_native_checkout_pending', '1'); } catch (_e) { /* noop */ }
            await openCheckout(res.data.checkout_url);
            // Start server-side polling 3s after the tab opens (gives Viva
            // time to render its form so we don't hammer the backend during
            // the human-typing phase).
            pollTimer = setTimeout(pollForConnection, 3000);
          } catch (browserErr) {
            // Plugin refused to open the sheet — clear the listener and
            // reset busy so the user can try again instead of being stuck.
            unsubscribe();
            stopPolling();
            try { localStorage.removeItem('allure_native_checkout_pending'); } catch (_e2) { /* noop */ }
            setBusy(false);
            toast.error(browserErr?.message || 'Could not open the payment window');
          }
          return;
        }
        // Web / PWA: same-tab redirect (Viva Success URL bounces back to
        // /payment-complete which handles the finalize step).
        await openCheckout(res.data.checkout_url);
        return;
      }
      toast.error('Could not start card setup');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not start card setup';
      toast.error(detail);
    } finally {
      // Only reset for the redirect (web) path — the in-app path clears
      // `busy` inside the onCheckoutClosed handler above.
      if (!isNativeCheckoutSupported()) setBusy(false);
    }
  };

  const handleRemove = async () => {
    setConfirmRemoveOpen(false);
    setBusy(true);
    try {
      await axios.delete(`${API}/guest/payment-method`, { headers: authHeaders() });
      toast.success('Payment method removed');
      await refresh();
    } catch (err) {
      toast.error('Failed to remove payment method');
    } finally {
      setBusy(false);
    }
  };

  return (
    <HostSettingsLayout
      title="Payment Method"
      dataTestId="guest-payment-method-page"
      onBack={() => navigate('/guest/settings')}
    >
      {loading ? (
        <div data-testid="guest-payment-method-loading" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={28} className="animate-spin" color="#703FF6" />
        </div>
      ) : card ? (
        <>
          <HostSettingsCard>
            <div
              data-testid="guest-payment-method-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '6px 4px',
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #703FF6 0%, #4A20C9 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CreditCard size={22} color="#FFFFFF" strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
                  {cardBrandLabel(card.brand)} •••• {card.last4 || '••••'}
                </div>
                {(card.expiry_month && card.expiry_year) ? (
                  <div style={{ fontFamily: FONT, fontSize: 13, color: '#7A7590', marginTop: 2 }}>
                    Expires {formatExpiry(card.expiry_month, card.expiry_year)}
                  </div>
                ) : null}
              </div>
            </div>
          </HostSettingsCard>

          <div
            style={{
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'rgba(31, 142, 42, 0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <ShieldCheck size={18} color="#1F8E2A" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
              Your card is securely stored by Viva Wallet. Allure never sees or stores card numbers.
            </div>
          </div>

          <button
            data-testid="guest-payment-method-remove-button"
            onClick={() => setConfirmRemoveOpen(true)}
            disabled={busy}
            style={{
              marginTop: 18,
              width: '100%',
              padding: '12px 16px',
              border: '1px solid rgba(229, 57, 53, 0.32)',
              borderRadius: 14,
              background: 'rgba(229, 57, 53, 0.06)',
              color: '#C92016',
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            <Trash2 size={16} strokeWidth={2} />
            Remove payment method
          </button>
        </>
      ) : (
        <>
          {isDemoEnv && (
            <div
              data-testid="guest-payment-closed-beta-notice"
              style={{
                marginBottom: 14,
                padding: '14px 16px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12) 0%, rgba(255, 152, 0, 0.10) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.32)',
              }}
            >
              <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: '#8A4500', marginBottom: 6 }}>
                Closed Beta — no real charges
              </div>
              <div style={{ fontFamily: FONT, fontSize: 13, color: '#5A3A00', lineHeight: 1.5 }}>
                Use Viva Wallet test card <strong>4111 1111 1111 1111</strong>, any future expiry (e.g. 12/30), CVV <strong>123</strong>. The €0.50 verification hold is simulated and never billed.
              </div>
            </div>
          )}
          <div
            data-testid="guest-payment-method-empty"
            style={{
              position: 'relative',
              padding: '28px 20px 26px',
              borderRadius: 24,
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              boxShadow: 'var(--premium-shadow-main)',
              textAlign: 'center',
              overflow: 'hidden',
            }}
          >
            {/* Stylized mini credit card — sets the trust tone before the CTA. */}
            <div
              aria-hidden
              style={{
                width: 200,
                height: 124,
                margin: '4px auto 20px',
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1A1330 0%, #3B1F7A 55%, #703FF6 100%)',
                boxShadow: '0 18px 40px rgba(112, 63, 246, 0.32), 0 2px 6px rgba(26, 19, 48, 0.4)',
                position: 'relative',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                color: '#FFFFFF',
                overflow: 'hidden',
              }}
            >
              {/* Soft glossy sheen */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(120% 60% at 0% 0%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 55%)',
                  pointerEvents: 'none',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                {/* EMV chip */}
                <div
                  style={{
                    width: 32,
                    height: 24,
                    borderRadius: 5,
                    background: 'linear-gradient(135deg, #E9C46A 0%, #B8862F 100%)',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                    position: 'relative',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 3, borderRadius: 3, border: '1px solid rgba(0,0,0,0.25)' }} />
                </div>
                <Wifi size={18} strokeWidth={2} style={{ transform: 'rotate(90deg)', opacity: 0.9 }} />
              </div>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 13,
                    letterSpacing: 2,
                    opacity: 0.92,
                    marginBottom: 6,
                  }}
                >
                  •••• •••• •••• ••••
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div
                    style={{
                      fontFamily: FONT,
                      fontSize: 10,
                      opacity: 0.75,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                    }}
                  >
                    Your card
                  </div>
                  <div
                    style={{
                      fontFamily: FONT,
                      fontStyle: 'italic',
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: 0.5,
                    }}
                  >
                    Allure
                  </div>
                </div>
              </div>
            </div>

            <div style={{ fontFamily: FONT, fontSize: 19, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
              Add your card to pay in a tap
            </div>
            <div style={{ fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 300, margin: '0 auto 18px' }}>
              Save a card once, then pay at any Allure partner venue with one tap — no card details to re-type.
            </div>

            {/* Trust badges row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                flexWrap: 'wrap',
                paddingTop: 14,
                borderTop: '1px solid var(--hairline)',
              }}
            >
              {[
                { icon: <Lock size={13} strokeWidth={2.2} />, label: '256-bit SSL' },
                { icon: <ShieldCheck size={13} strokeWidth={2.2} />, label: 'PCI-DSS' },
                { icon: <CreditCard size={13} strokeWidth={2.2} />, label: 'Viva Wallet' },
              ].map((badge) => (
                <div
                  key={badge.label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontFamily: FONT,
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-2)',
                    letterSpacing: 0.2,
                  }}
                >
                  <span style={{ color: '#1F8E2A', display: 'inline-flex' }}>{badge.icon}</span>
                  {badge.label}
                </div>
              ))}
            </div>
          </div>

          <button
            data-testid="guest-payment-method-add-button"
            onClick={handleAddCard}
            disabled={busy}
            style={{
              marginTop: 20,
              width: '100%',
              padding: '15px 18px',
              border: 'none',
              borderRadius: 9999,
              background: busy
                ? 'linear-gradient(135deg, #8A6BFF 0%, #6B4AE0 100%)'
                : 'linear-gradient(135deg, #703FF6 0%, #4A20C9 100%)',
              color: '#FFFFFF',
              fontFamily: FONT,
              fontSize: 17,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 10px 24px rgba(74, 32, 201, 0.35), 0 2px 4px rgba(74, 32, 201, 0.2)',
              opacity: busy ? 0.85 : 1,
              transition: 'transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
              letterSpacing: 0.2,
            }}
          >
            {busy ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Opening secure checkout…
              </>
            ) : (
              <>
                <Lock size={16} strokeWidth={2.4} />
                Add card securely
              </>
            )}
          </button>

          <button
            data-testid="guest-payment-method-recover-button"
            onClick={() => reconcile({ silent: false })}
            disabled={reconciling || busy}
            style={{
              marginTop: 12,
              width: '100%',
              background: 'none',
              border: 'none',
              color: 'var(--text-2)',
              fontFamily: FONT,
              fontSize: 13,
              textDecoration: 'underline',
              cursor: reconciling ? 'wait' : 'pointer',
              opacity: reconciling ? 0.5 : 1,
            }}
          >
            {reconciling ? 'Checking for previous payment…' : 'Already paid? Recover my card'}
          </button>

          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'rgba(31, 142, 42, 0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <ShieldCheck size={18} color="#1F8E2A" strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
              Cards are stored by <strong>Viva Wallet</strong> under PCI-DSS. Allure never sees or stores card numbers. A €0.50 verification hold is placed and released right after.
            </div>
          </div>
        </>
      )}

      <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <AlertDialogContent
          data-testid="guest-payment-method-remove-confirm-dialog"
          style={{ fontFamily: FONT }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: FONT, color: 'var(--text)' }}>
              Remove payment method?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontFamily: FONT, color: '#5A5570' }}>
              You&apos;ll need to add a card again before you can pay at Allure partner venues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="guest-payment-method-remove-cancel"
              style={{ fontFamily: FONT }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="guest-payment-method-remove-confirm"
              onClick={handleRemove}
              style={{
                fontFamily: FONT,
                background: '#C92016',
                color: '#FFFFFF',
              }}
            >
              Remove card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HostSettingsLayout>
  );
}
